import { useCallback, useEffect, useRef } from "react";
import config from "../../../config.js";
import { calculateBearing } from "../../../components/driverConsole/commonFunctions.js";
import {
  RECONNECT_INTERVAL,
  MAX_RECONNECT_ATTEMPTS,
  MAX_HISTORY_LENGTH,
} from "./mapConfig";
import {
  extractLiveTelemetry,
  mergeLiveTelemetry,
  smoothVehicleSpeed,
} from "./liveTelemetry";

export const useVehicleTracking = ({
  vehicle,
  findNearestPointOnRoute,
  detectVehicleMovement,
  calculateDistance,
  directionsRef,
  map,
  setPosition,
  setHeading,
  setSpeed,
  setIsMoving,
  setLastMovedPosition,
  setStoppedDuration,
  setSnappedPosition,
  setPositionHistory,
  setIsOnline,
  setIsReconnecting,
  setTelemetry,
  setRouteDeviationEvent,
  setRouteDeviation,
}) => {
  const isMountedRef = useRef(true);
  const eventSourceRef = useRef(null);
  const reconnectAttemptsRef = useRef(0);
  const positionHistoryRef = useRef({});
  // Refs avoid stale closures in the long-lived SSE handler (same pattern as VehicleScreen)
  const headingRef = useRef(0);
  const lastMovedPositionRef = useRef(null);
  const lastGpsRef = useRef(null);
  const lastSpeedRef = useRef(null);

  const fetchLocation = useCallback(() => {
    if (!vehicle?.device_id || eventSourceRef.current) return;

    const connectSSE = () => {
      const query = `vehicleId=${vehicle.device_id}`;
      const sseUrl = `${config.baseURL}/redis/stream?${query}`;
      const eventSource = new EventSource(sseUrl);
      eventSourceRef.current = eventSource;

      eventSource.onmessage = (event) => {
        try {
          const locationData = JSON.parse(event.data);
          console.log("SSE Data Received:", locationData);
          if (!isMountedRef.current) return;

          const rawTimestamp =
            locationData?.timestamp ??
            locationData?.recordedAt ??
            locationData?.createdAt;
          let parsedTimestamp = Date.now();
          if (
            rawTimestamp !== undefined &&
            rawTimestamp !== null &&
            rawTimestamp !== ""
          ) {
            const numeric = Number(rawTimestamp);
            if (Number.isFinite(numeric)) {
              parsedTimestamp = numeric < 1e12 ? numeric * 1000 : numeric;
            } else {
              const fromDate = new Date(rawTimestamp).getTime();
              if (Number.isFinite(fromDate)) parsedTimestamp = fromDate;
            }
          }

          const newPosition = {
            lat: parseFloat(locationData?.Latitude || locationData?.latitude),
            lng: parseFloat(locationData?.Longitude || locationData?.longitude),
            timestamp: parsedTimestamp,
          };

          if (!newPosition.lat || !newPosition.lng) {
            console.error("Invalid position data:", newPosition);
            return;
          }

          setIsOnline(true);
          setIsReconnecting(false);
          reconnectAttemptsRef.current = 0;

          if (!positionHistoryRef.current[vehicle.device_id]) {
            positionHistoryRef.current[vehicle.device_id] = [];
          }

          positionHistoryRef.current[vehicle.device_id].push({
            lat: newPosition.lat,
            lng: newPosition.lng,
            timestamp: Date.now(),
          });

          const history = positionHistoryRef.current[vehicle.device_id];
          if (history.length > MAX_HISTORY_LENGTH) {
            positionHistoryRef.current[vehicle.device_id] = history.slice(
              -MAX_HISTORY_LENGTH,
            );
          }

          // Heading from previous GPS → current (keep last heading if move is tiny)
          const HEADING_MOVE_M = 5; // match VehicleScreen
          if (lastGpsRef.current) {
            const prevPos = lastGpsRef.current;
            const distMoved = calculateDistance(prevPos, newPosition);
            if (distMoved > HEADING_MOVE_M) {
              const bearing = calculateBearing(
                { latitude: prevPos.lat, longitude: prevPos.lng },
                { latitude: newPosition.lat, longitude: newPosition.lng },
              );
              headingRef.current = bearing;
              setHeading(bearing);
            }
            // else: keep headingRef.current — do not reset to 0
          }
          lastGpsRef.current = newPosition;

          setPositionHistory((prev) => [...prev, newPosition].slice(-25));

          const moving = detectVehicleMovement(
            newPosition,
            lastMovedPositionRef.current,
          );
          if (moving) {
            setIsMoving(true);
            lastMovedPositionRef.current = newPosition;
            setLastMovedPosition(newPosition);
            setStoppedDuration(0);
          } else {
            setIsMoving(false);
            setStoppedDuration((prev) => prev + 10);
          }

          const liveTelemetry = extractLiveTelemetry(locationData);
          if (liveTelemetry.speed != null) {
            const smoothed = smoothVehicleSpeed(
              lastSpeedRef.current,
              liveTelemetry.speed,
              { moving },
            );
            lastSpeedRef.current = smoothed;
            liveTelemetry.speed = smoothed;
            setSpeed(smoothed);
          }
          if (typeof setTelemetry === "function") {
            setTelemetry((previous) =>
              mergeLiveTelemetry(previous, liveTelemetry),
            );
          }

          if (
            typeof setRouteDeviationEvent === "function" &&
            locationData?.routeDeviationEvent
          ) {
            setRouteDeviationEvent(locationData.routeDeviationEvent);
          }
          if (typeof setRouteDeviation === "function") {
            setRouteDeviation(locationData?.routeDeviation || null);
          }

          // Real GPS for marker; snap kept separately for route progress
          setPosition(newPosition);

          if (directionsRef.current) {
            const snapped = findNearestPointOnRoute(
              newPosition,
              directionsRef.current,
            );
            setSnappedPosition(snapped);
          } else {
            setSnappedPosition(null);
          }

          if (map) {
            map.panTo(newPosition);
          }
        } catch (error) {
          console.error("Error processing SSE data:", error);
        }
      };

      eventSource.onerror = () => {
        setIsOnline(false);
        setIsReconnecting(true);
        eventSource.close();
        eventSourceRef.current = null;
        if (reconnectAttemptsRef.current < MAX_RECONNECT_ATTEMPTS) {
          reconnectAttemptsRef.current += 1;
          console.log(
            `Reconnecting SSE, attempt ${reconnectAttemptsRef.current}`,
          );
          setTimeout(connectSSE, RECONNECT_INTERVAL);
        } else {
          console.error("Max reconnect attempts reached");
          setIsReconnecting(false);
        }
      };
    };

    connectSSE();

    return () => {
      if (eventSourceRef.current) {
        eventSourceRef.current.close();
        eventSourceRef.current = null;
      }
    };
  }, [
    vehicle?.device_id,
    findNearestPointOnRoute,
    detectVehicleMovement,
    calculateDistance,
    directionsRef,
    map,
    setPosition,
    setHeading,
    setSpeed,
    setIsMoving,
    setLastMovedPosition,
    setStoppedDuration,
    setSnappedPosition,
    setPositionHistory,
    setIsOnline,
    setIsReconnecting,
    setTelemetry,
    setRouteDeviationEvent,
    setRouteDeviation,
  ]);

  useEffect(() => {
    if (!vehicle?.device_id) return;
    isMountedRef.current = true;
    headingRef.current = 0;
    lastMovedPositionRef.current = null;
    lastGpsRef.current = null;
    lastSpeedRef.current = null;
    positionHistoryRef.current = {};
    const cleanup = fetchLocation();
    return () => {
      isMountedRef.current = false;
      if (typeof cleanup === "function") cleanup();
    };
  }, [vehicle?.device_id, fetchLocation]);

  return { isMountedRef, eventSourceRef };
};
